import React, { useState } from "react";
import {
    Button,
    Typography,
    useTheme,
    CircularProgress,
    Stack,
    Tooltip
} from "@mui/material";
import { useAtom } from "jotai";
import {
    googleCalRefetchIntervalAtom,
    googleIntegrationCalInfoAtom,
    selectedProviderAtom,
    selectedUserIdAtom,
    updateGoogleIntegrationCalInfoAtom,
    userPageSnackbarAtom
} from "../../atoms/usersTable";
import WarningTriangleIcon from "../../images/WarningTriangleIcon.svg";
import { getRefData } from "../../graphql/queries";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
    delApi,
    graphql,
    graphqlOperation,
    postApi
} from "../../modules/AmplifyServices";
import { jsonParse } from "../../utils";
import {
    GOOGLE_CAL_INTEGRATION_STATUS_SYNC_FAILED,
    GOOGLE_CAL_INTEGRATION_STATUS_SYNCING,
    GOOGLE_CAL_INTEGRATION_QUEUED_FOR_SYNCING,
    GOOGLE_CAL_INTEGRATION_ID,
    GOOGLE_CAL_INTEGRATION_STATUS_AUTHED
} from "../../utils/Constants";
import {
    isMbxUserManager,
    isMbxUserVerifiedStaff,
    mbxUserAtom
} from "../../atoms/atoms";
import SmallSizedModal from "../../utils/UI/SmallSizedModal";
import {
    getGoogleCalIntegrationView,
    VIEW_CONNECT,
    VIEW_CONTINUE_SETUP
} from "./googleCalIntegrationView";
import { Box } from "@mui/system";
import GoogleCalSVG from "../../images/google-calendar.svg";
import LoadingButton from "@mui/lab/LoadingButton";
// for local dev work will be handy in push notifications work
// const fetchGrok = async (endpoint, method = "GET", body) => {
//     const myHeaders = new Headers();
//     myHeaders.append("ngrok-skip-browser-warning", "69420");
//     const respJ = await fetch(
//         `${process.env.REACT_APP_GOOGLE_NGROK_URL}/${endpoint}`,
//         { headers: myHeaders, method, ...(body && { body }) }
//     );
//     return await respJ.json();
// };

export const GoogleCalIntegration = () => {
    const [isMutatingIntegration, setIsMutatingIntegration] = useState(false);
    const [, setUserPageSnackbar] = useAtom(userPageSnackbarAtom);
    const [refetchInterval, setRefetchInterval] = useAtom(
        googleCalRefetchIntervalAtom
    );
    const [isVerifiedStaff] = useAtom(isMbxUserVerifiedStaff);
    const [isLoggedInUserManager] = useAtom(isMbxUserManager);
    const [, setGoogleCalInfo] = useAtom(updateGoogleIntegrationCalInfoAtom);
    const [googleCalInfo] = useAtom(googleIntegrationCalInfoAtom);

    const [showDeleteIntegrationModal, setShowDeleteIntegrationModal] =
        useState(false);
    const [showSyncDirectionModal, setShowSyncDirectionModal] = useState(false);
    const [selectedProvider] = useAtom(selectedProviderAtom);
    const theme = useTheme();
    const [mbxUser] = useAtom(mbxUserAtom);
    const [selectedUserId] = useAtom(selectedUserIdAtom);
    const showIntegrationStatusOnly =
        mbxUser?.role === "MARKETBOX_ADMIN" ||
        (isVerifiedStaff && selectedUserId !== mbxUser?.id);
    const isManagerView =
        isLoggedInUserManager && mbxUser.id !== selectedUserId;
    const isViewingOtherUser = isManagerView || showIntegrationStatusOnly;
    const integrationView = getGoogleCalIntegrationView({
        googleCalInfo,
        showIntegrationStatusOnly,
        isManagerView,
        isMutatingIntegration
    });

    const confirmDeleteMutation = useMutation({
        mutationFn: async () => {
            setIsMutatingIntegration(true);
            await delApi(
                "integrationapi",
                `/google-cal/synced-accounts/${selectedProvider.id}`
            );
            await refetchGoogleIntegrationCalInfo();
            setIsMutatingIntegration(false);
            setShowDeleteIntegrationModal(false);
            setUserPageSnackbar({
                open: true,
                message: "Google Calendar successfully disconnected",
                severity: "success"
            });
            return;
        }
    });

    const {
        data: googleIntegrationCalInfo,
        isFetching: isLoadingGoogleCalIntegration,
        refetch: refetchGoogleIntegrationCalInfo
    } = useQuery({
        refetchInterval,
        queryKey: ["google-cal-integration"],
        queryFn: async () => {
            try {
                const { data, errors } = await graphql(
                    graphqlOperation(getRefData, {
                        refType: `integration|${selectedProvider.id}`,
                        refName: GOOGLE_CAL_INTEGRATION_ID
                    })
                );
                if (errors) throw errors;
                const googleIntegrationCalInfo = jsonParse(
                    data?.getRefData?.overrideValue,
                    null
                );
                if (
                    googleIntegrationCalInfo?.status !==
                        GOOGLE_CAL_INTEGRATION_STATUS_SYNCING &&
                    googleIntegrationCalInfo?.status !==
                        GOOGLE_CAL_INTEGRATION_QUEUED_FOR_SYNCING
                ) {
                    setRefetchInterval(false);
                }

                setGoogleCalInfo(googleIntegrationCalInfo);
                return googleIntegrationCalInfo;
            } catch (e) {
                console.log(e);
            }
        }
    });
    // TODO: can this be a tan stack mutation ?
    const backEndFlowHandler = async () => {
        if (!googleIntegrationCalInfo) {
            setIsMutatingIntegration(true);

            //Google Popup config
            const dualScreenLeft =
                window.screenLeft !== undefined
                    ? window.screenLeft
                    : window.screenX;
            const dualScreenTop =
                window.screenTop !== undefined
                    ? window.screenTop
                    : window.screenY;
            const width = window.innerWidth
                ? window.innerWidth
                : document.documentElement.clientWidth
                  ? document.documentElement.clientWidth
                  : window.screen.availWidth;
            const height = window.innerHeight
                ? window.innerHeight
                : document.documentElement.clientHeight
                  ? document.documentElement.clientHeight
                  : window.screen.availHeight;
            const systemZoom = width / window.screen.availWidth;
            const left = (width - 1044) / 2 / systemZoom + dualScreenLeft;
            const top = (height - 717) / 2 / systemZoom + dualScreenTop;
            const popupWidth = 1044;
            const popupHeight = 717;
            const popupWindow = window.open(
                `${process.env.REACT_APP_INTEGRATIONS_URL}/google-cal/auth?providerId=${selectedProvider.id}`,
                "google-oauth-popup", // Popup window name,
                `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=no`
            );

            const checkIfClosed = () => {
                if (popupWindow.closed) {
                    setIsMutatingIntegration(false);
                    clearInterval(timer);
                }
            };

            const timer = setInterval(checkIfClosed, 500);
            window.addEventListener("message", async (event) => {
                if (event.origin !== process.env.REACT_APP_INTEGRATIONS_URL) {
                    // Ignore messages from unknown origins
                    return;
                }
                // OAuth successful
                if (event.data.status === "googleAuthSuccess") {
                    setIsMutatingIntegration(false);
                    setShowSyncDirectionModal(true);
                } else if (event.data.status === "googleAuthError") {
                    console.error("OAuth failed:", event.data.message);
                    setUserPageSnackbar({
                        open: true,
                        message: "Failed to authenticate with google cal",
                        severity: "error"
                    });
                    setIsMutatingIntegration(false);
                }
            });
            return;
        } else {
            setShowDeleteIntegrationModal(true);
        }
    };

    const handleSyncDirectionSelect = async (syncDirection) => {
        try {
            setIsMutatingIntegration(true);
            setShowSyncDirectionModal(false);
            await postApi(
                "integrationapi",
                `/google-cal/synced-accounts/${selectedProvider.id}`,
                {
                    body: {
                        ...(syncDirection === "one-way" && { syncDirection })
                    }
                }
            );
            await refetchGoogleIntegrationCalInfo();
            setRefetchInterval(3000);
        } catch (e) {
            console.log("Error starting sync:", e);
            setUserPageSnackbar({
                open: true,
                message: "Failed to start Google Calendar sync",
                severity: "error"
            });
        } finally {
            setIsMutatingIntegration(false);
        }
    };

    const getButtonComponent = () => {
        switch (integrationView.view) {
            case VIEW_CONNECT:
                return (
                    <ConnectButton
                        disabled={integrationView.disabled}
                        onClick={backEndFlowHandler}
                        isMutating={isMutatingIntegration}
                        tooltip={integrationView.tooltip}
                    />
                );
            case VIEW_CONTINUE_SETUP:
                // Google OAuth finished but the sync direction was never
                // chosen (e.g. the page was reloaded before the modal was
                // answered). Let the user finish setup or back out.
                return (
                    <Stack
                        direction="row"
                        gap="0.75rem"
                        alignItems="center"
                        flexWrap="wrap"
                    >
                        <ContinueSetupButton
                            disabled={integrationView.disabled}
                            onClick={() => setShowSyncDirectionModal(true)}
                            tooltip={integrationView.tooltip}
                        />
                        <DisconnectButton
                            variant="outlined"
                            disabled={!integrationView.canDisconnect}
                            onClick={() => setShowDeleteIntegrationModal(true)}
                            tooltip={
                                isViewingOtherUser
                                    ? "You cannot disconnect another user's Google Calendar"
                                    : ""
                            }
                        />
                    </Stack>
                );
            default:
                return (
                    <DisconnectButton
                        disabled={integrationView.disabled}
                        onClick={() => setShowDeleteIntegrationModal(true)}
                        tooltip={integrationView.tooltip}
                    />
                );
        }
    };

    const getStatusComponent = () => {
        if (!googleCalInfo) {
            return null;
        }

        switch (googleCalInfo.status) {
            case GOOGLE_CAL_INTEGRATION_STATUS_SYNCING:
                return <SyncInProgress theme={theme} />;
            case GOOGLE_CAL_INTEGRATION_QUEUED_FOR_SYNCING:
                return <SyncInProgress theme={theme} />;
            case GOOGLE_CAL_INTEGRATION_STATUS_SYNC_FAILED:
                return <SyncFailedMessage theme={theme} />;
            case GOOGLE_CAL_INTEGRATION_STATUS_AUTHED:
                return <SetupIncompleteMessage theme={theme} />;
            default:
                return null;
        }
    };

    return (
        <>
            <Stack justifyContent="flex-start" alignItems={"center"}>
                {getButtonComponent()}
                {getStatusComponent()}
            </Stack>
            <SmallSizedModal
                open={showDeleteIntegrationModal}
                onClose={(e, reason) => {
                    if (reason && reason === "backdropClick") return;
                    setShowDeleteIntegrationModal(true);
                }}
                headingIcon={WarningTriangleIcon}
                headingText={"Disconnect Google Calendar"}
            >
                <Box mt={2}>
                    <Typography variant="body2">
                        If you disconnect your Google Calendar, any imported
                        bookings will no longer be used for travel time
                        calculations and will be permanently removed from
                        MarketBox — do you wish to continue?
                    </Typography>
                </Box>
                <Box
                    mt={2}
                    gap={"1.25rem"}
                    display={"flex"}
                    justifyContent={"flex-end"}
                >
                    <Button
                        variant="outlined"
                        onClick={() => setShowDeleteIntegrationModal(false)}
                        disabled={confirmDeleteMutation.isLoading}
                        sx={{
                            border: `1px solid ${theme.palette.primary.main}`
                        }}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained"
                        onClick={confirmDeleteMutation.mutate}
                        loading={confirmDeleteMutation.isLoading}
                    >
                        Disconnect
                    </LoadingButton>
                </Box>
            </SmallSizedModal>
            <SmallSizedModal
                open={showSyncDirectionModal}
                onClose={(e, reason) => {
                    if (reason && reason === "backdropClick") return;
                }}
                headingIcon={GoogleCalSVG}
                headingText={"Choose Sync Direction"}
            >
                <Box mt={2}>
                    <Typography variant="body2">
                        How would you like to sync your Google Calendar with
                        MarketBox?
                    </Typography>
                </Box>
                <Box
                    mt={2}
                    gap={"1rem"}
                    display={"flex"}
                    flexDirection={"column"}
                >
                    <Button
                        variant="contained"
                        onClick={() => handleSyncDirectionSelect("two-way")}
                        disabled={isMutatingIntegration}
                        fullWidth
                    >
                        Two-Way Sync
                    </Button>
                    <Typography
                        variant="caption"
                        sx={{ mt: -0.5, color: "text.secondary" }}
                    >
                        Google Calendar events will appear in MarketBox, and
                        MarketBox bookings will appear in Google Calendar.
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={() => handleSyncDirectionSelect("one-way")}
                        disabled={isMutatingIntegration}
                        fullWidth
                    >
                        One-Way Sync (Google to MarketBox only)
                    </Button>
                    <Typography
                        variant="caption"
                        sx={{ mt: -0.5, color: "text.secondary" }}
                    >
                        Only Google Calendar events will be imported into
                        MarketBox. MarketBox bookings will not be pushed to
                        Google Calendar.
                    </Typography>
                </Box>
            </SmallSizedModal>
        </>
    );
};

const ConnectButton = ({ disabled, onClick, isMutating, tooltip }) => (
    <Tooltip
        title={tooltip}
        placement="top"
        componentsProps={{ tooltip: { sx: { maxWidth: "none" } } }}
    >
        <div style={{ width: "fit-content" }}>
            <Button
                onClick={onClick}
                disabled={disabled}
                sx={{
                    lineHeight: "17px",
                    fontWeight: "500",
                    gap: "9px"
                }}
                variant="contained"
            >
                Connect Google Calendar
                <GoogleIcon disabled={disabled} />
            </Button>
        </div>
    </Tooltip>
);

const DisconnectButton = ({
    disabled,
    onClick,
    tooltip,
    variant = "contained"
}) => (
    <Tooltip
        title={tooltip}
        placement="top"
        componentsProps={{ tooltip: { sx: { maxWidth: "none" } } }}
    >
        <div style={{ width: "fit-content" }}>
            <Button
                onClick={onClick}
                sx={{
                    lineHeight: "17px",
                    fontWeight: "500",
                    gap: "9px"
                }}
                variant={variant}
                disabled={disabled}
            >
                Disconnect Google Calendar
                <GoogleIcon disabled={disabled} />
            </Button>
        </div>
    </Tooltip>
);

const ContinueSetupButton = ({ disabled, onClick, tooltip }) => (
    <Tooltip
        title={tooltip}
        placement="top"
        componentsProps={{ tooltip: { sx: { maxWidth: "none" } } }}
    >
        <div style={{ width: "fit-content" }}>
            <Button
                onClick={onClick}
                disabled={disabled}
                sx={{
                    lineHeight: "17px",
                    fontWeight: "500",
                    gap: "9px"
                }}
                variant="contained"
            >
                Choose Sync Direction
                <GoogleIcon disabled={disabled} />
            </Button>
        </div>
    </Tooltip>
);

const SetupIncompleteMessage = ({ theme }) => (
    <Typography
        sx={{
            textTransform: "none",
            lineHeight: "16px",
            fontWeight: "500",
            color: theme.palette.grey.light
        }}
    >
        Google authorized – choose a sync direction to finish connecting, or
        disconnect
    </Typography>
);

const SyncInProgress = ({ theme }) => (
    <>
        <Typography
            sx={{
                textTransform: "none",
                lineHeight: "16px",
                fontWeight: "500",
                color: theme.palette.grey.light
            }}
        >
            Sync in progress – please check back later
            <CircularProgress size={12} sx={{ marginLeft: "9px" }} />
        </Typography>
    </>
);

const SyncFailedMessage = ({ theme }) => (
    <Typography
        sx={{
            textTransform: "none",
            lineHeight: "16px",
            color: theme.palette.warning.main
        }}
    >
        Sync failed – please contact MarketBox support
    </Typography>
);

const GoogleIcon = ({ disabled }) => (
    <img
        src={GoogleCalSVG}
        alt="google-cal"
        style={{
            width: 18,
            height: 18,
            mixBlendMode: disabled ? "luminosity" : ""
        }}
    />
);
export default GoogleCalIntegration;
